/**
 * oauth-result.ts — đọc kết quả redirect OAuth (Zalo OA, Facebook, TikTok Shop, Shopee) trên query string.
 *
 * Backend chuyển về `/integrations?...` (mặc định) hoặc `/onboarding?...` (khi bắt đầu từ trang Bắt đầu —
 * `returnTo` trong state). Hàm thuần: trả thông báo cần hiện + kênh, trang gọi tự hiện toast / điều hướng.
 */
import { i18n } from '@/lib/i18n'

export type OAuthChannel = 'zalo-oa' | 'facebook' | 'tiktok-shop' | 'shopee'

export interface OAuthNotice {
  type: 'success' | 'error' | 'warning'
  text: string
  /** true khi thông báo này là do đã đạt hạn mức kênh chính thức của gói — trang gọi hiện popup điều
   * hướng mua thêm/nâng cấp gói thay vì chỉ toast (xem `useQuotaExceededDialog`). */
  quotaExceeded?: boolean
}

export interface OAuthResult {
  channel: OAuthChannel
  ok: boolean
  notices: OAuthNotice[]
}

function quotaExceededMessage(): string {
  return i18n.t('sharedMisc:oauthResult.quotaExceeded')
}

function oaErrorText(code: string): string {
  if (code === 'quota_exceeded') return quotaExceededMessage()
  const key = `sharedMisc:oauthResult.oa.errors.${code}`
  return i18n.exists(key) ? i18n.t(key) : i18n.t('sharedMisc:oauthResult.oa.errorFallback', { code })
}

function fbErrorText(code: string): string {
  if (code === 'quota_exceeded') return quotaExceededMessage()
  const key = `sharedMisc:oauthResult.fb.errors.${code}`
  return i18n.exists(key) ? i18n.t(key) : i18n.t('sharedMisc:oauthResult.fb.errorFallback', { code })
}

function tiktokErrorText(code: string): string {
  if (code === 'quota_exceeded') return quotaExceededMessage()
  const key = `sharedMisc:oauthResult.tiktok.errors.${code}`
  return i18n.exists(key) ? i18n.t(key) : i18n.t('sharedMisc:oauthResult.tiktok.errorFallback', { code })
}

function shopeeErrorText(code: string): string {
  if (code === 'quota_exceeded') return quotaExceededMessage()
  const key = `sharedMisc:oauthResult.shopee.errors.${code}`
  return i18n.exists(key) ? i18n.t(key) : i18n.t('sharedMisc:oauthResult.shopee.errorFallback', { code })
}

/** Tham số kết quả OAuth — dọn khỏi URL sau khi đã báo. */
export const OAUTH_RESULT_PARAMS = [
  'oa_connected', 'oa_error',
  'fb_connected', 'fb_error', 'fb_conflict', 'fb_quota_blocked',
  'tiktok_connected', 'tiktok_error', 'tiktok_quota_blocked',
  'shopee_connected', 'shopee_error', 'shopee_conflict', 'shopee_quota_blocked',
  'shop',
] as const

export function parseOAuthResult(sp: Pick<URLSearchParams, 'get' | 'has'>): OAuthResult | null {
  if (sp.has('oa_connected')) {
    return { channel: 'zalo-oa', ok: true, notices: [{ type: 'success', text: i18n.t('sharedMisc:oauthResult.oa.connected') }] }
  }
  const oaError = sp.get('oa_error')
  if (oaError) {
    return { channel: 'zalo-oa', ok: false, notices: [{ type: 'error', text: oaErrorText(oaError), quotaExceeded: oaError === 'quota_exceeded' }] }
  }

  const fbConnected = sp.get('fb_connected')
  if (fbConnected) {
    const n = Number(fbConnected)
    const notices: OAuthNotice[] = [{ type: 'success', text: n > 0 ? i18n.t('sharedMisc:oauthResult.fb.connectedCount', { n }) : i18n.t('sharedMisc:oauthResult.fb.connectedDone') }]
    const conflict = sp.get('fb_conflict')
    if (conflict && Number(conflict) > 0) notices.push({ type: 'warning', text: i18n.t('sharedMisc:oauthResult.fb.conflict', { count: conflict }) })
    const quotaBlocked = sp.get('fb_quota_blocked')
    if (quotaBlocked && Number(quotaBlocked) > 0) notices.push({ type: 'warning', text: i18n.t('sharedMisc:oauthResult.fb.quotaBlocked', { count: quotaBlocked }) })
    return { channel: 'facebook', ok: n > 0, notices }
  }
  const fbError = sp.get('fb_error')
  if (fbError) {
    return { channel: 'facebook', ok: false, notices: [{ type: 'error', text: fbErrorText(fbError), quotaExceeded: fbError === 'quota_exceeded' }] }
  }

  if (sp.has('tiktok_connected')) {
    const shop = sp.get('shop')
    const notices: OAuthNotice[] = [{ type: 'success', text: shop ? i18n.t('sharedMisc:oauthResult.tiktok.connectedShop', { shop }) : i18n.t('sharedMisc:oauthResult.tiktok.connectedDone') }]
    const quotaBlocked = sp.get('tiktok_quota_blocked')
    if (quotaBlocked && Number(quotaBlocked) > 0) notices.push({ type: 'warning', text: i18n.t('sharedMisc:oauthResult.tiktok.quotaBlocked', { count: quotaBlocked }) })
    return { channel: 'tiktok-shop', ok: true, notices }
  }
  const tiktokError = sp.get('tiktok_error')
  if (tiktokError) {
    return {
      channel: 'tiktok-shop',
      ok: false,
      notices: [{ type: 'error', text: tiktokErrorText(tiktokError), quotaExceeded: tiktokError === 'quota_exceeded' }],
    }
  }

  const shopeeConnected = sp.get('shopee_connected')
  if (shopeeConnected) {
    const shop = sp.get('shop')
    const n = Number(shopeeConnected)
    const notices: OAuthNotice[] = [
      { type: 'success', text: n > 1 ? i18n.t('sharedMisc:oauthResult.shopee.connectedMulti', { n }) : shop ? i18n.t('sharedMisc:oauthResult.shopee.connectedShop', { shop }) : i18n.t('sharedMisc:oauthResult.shopee.connectedDone') },
    ]
    const conflict = sp.get('shopee_conflict')
    if (conflict && Number(conflict) > 0) notices.push({ type: 'warning', text: i18n.t('sharedMisc:oauthResult.shopee.conflict', { count: conflict }) })
    const quotaBlocked = sp.get('shopee_quota_blocked')
    if (quotaBlocked && Number(quotaBlocked) > 0) notices.push({ type: 'warning', text: i18n.t('sharedMisc:oauthResult.shopee.quotaBlocked', { count: quotaBlocked }) })
    return { channel: 'shopee', ok: true, notices }
  }
  const shopeeError = sp.get('shopee_error')
  if (shopeeError) {
    return { channel: 'shopee', ok: false, notices: [{ type: 'error', text: shopeeErrorText(shopeeError), quotaExceeded: shopeeError === 'quota_exceeded' }] }
  }
  return null
}
